import { Component, useState } from "react";
import type { ReactNode, ErrorInfo } from "react";
import { useRouteError, isRouteErrorResponse } from "react-router";
import {
  AlertCircle,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Home,
} from "lucide-react";
import * as Sentry from "@sentry/react";

const isDev = import.meta.env.DEV;

// ---------------------------------------------------------------------------
// Shared error display
// ---------------------------------------------------------------------------

/** Extracts a human-readable message + technical details from any error shape. */
function parseError(error: unknown): {
  message: string;
  stack?: string;
  status?: number;
  statusText?: string;
  componentStack?: string;
} {
  if (isRouteErrorResponse(error)) {
    return {
      message: error.data?.message ?? error.statusText ?? "Route error",
      status: error.status,
      statusText: error.statusText,
    };
  }
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack };
  }
  if (typeof error === "string") {
    return { message: error };
  }
  return { message: "An unexpected error occurred" };
}

/**
 * Collapsible stack trace panel — only rendered in dev mode.
 */
function DevDetails({
  message,
  stack,
  componentStack,
  status,
}: {
  message: string;
  stack?: string;
  componentStack?: string;
  status?: number;
}) {
  const [expanded, setExpanded] = useState(true);
  const [copied, setCopied] = useState(false);

  const fullTrace = [
    status ? `Status: ${status}` : "",
    `Error: ${message}`,
    stack ? `\nStack trace:\n${stack}` : "",
    componentStack ? `\nComponent stack:\n${componentStack}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  const handleCopy = () => {
    navigator.clipboard.writeText(fullTrace);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="w-full max-w-2xl mt-6 text-left">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors mb-2"
      >
        {expanded ? (
          <ChevronUp className="w-3.5 h-3.5" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5" />
        )}
        Error Details
      </button>

      {expanded && (
        <div className="relative rounded-lg border border-border bg-muted/50 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/80">
            <span className="text-[11px] font-mono text-muted-foreground">
              {status ? `${status} · ` : ""}
              {message.slice(0, 100)}
              {message.length > 100 ? "..." : ""}
            </span>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Copy error details"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  Copy
                </>
              )}
            </button>
          </div>
          <pre className="p-3 text-[11px] font-mono text-muted-foreground leading-relaxed overflow-x-auto max-h-[300px] overflow-y-auto whitespace-pre-wrap break-words">
            {fullTrace}
          </pre>
        </div>
      )}
    </div>
  );
}

/**
 * Friendly error UI shared by both the class ErrorBoundary and RouteErrorFallback.
 */
function ErrorDisplay({
  message,
  stack,
  componentStack,
  status,
  onRetry,
  onGoHome,
  onGoBack,
}: {
  message: string;
  stack?: string;
  componentStack?: string;
  status?: number;
  onRetry?: () => void;
  onGoHome?: () => void;
  onGoBack?: () => void;
}) {
  const is404 = status === 404;

  return (
    <div className="flex h-full min-h-[300px] items-center justify-center p-8">
      <div className="flex flex-col items-center text-center max-w-2xl">
        <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center mb-4">
          <AlertCircle className="w-6 h-6 text-error" />
        </div>

        <h2 className="text-lg font-semibold text-foreground mb-2">
          {is404 ? "Page not found" : "Something went wrong"}
        </h2>

        <p className="text-sm text-muted-foreground mb-6 max-w-md">
          {is404
            ? "The page you're looking for doesn't exist or has been moved."
            : isDev
              ? message
              : "An unexpected error occurred. Please try again or return to the home page. If this keeps happening, contact support."}
        </p>

        <div className="flex items-center gap-3">
          {onGoBack && (
            <button
              onClick={onGoBack}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Go Back
            </button>
          )}
          {onGoHome && (
            <button
              onClick={onGoHome}
              className="inline-flex items-center gap-2 px-4 py-2 border border-border text-foreground rounded-lg text-sm font-medium hover:bg-muted transition-colors"
            >
              <Home className="w-4 h-4" />
              Home
            </button>
          )}
          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Try Again
            </button>
          )}
        </div>

        {isDev && (stack || componentStack) && (
          <DevDetails
            message={message}
            stack={stack}
            componentStack={componentStack}
            status={status}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ErrorBoundary — class component (required by React for componentDidCatch)
// ---------------------------------------------------------------------------

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
    // Forward to Sentry — observability layer captures componentStack
    // in the React context for symbolicated stack traces.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? "" } },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const { error, componentStack } = this.state;
      return (
        <ErrorDisplay
          message={error?.message ?? "An unexpected error occurred"}
          stack={error?.stack}
          componentStack={componentStack ?? undefined}
          onRetry={this.handleReset}
          onGoHome={() => {
            this.handleReset();
            window.location.href = "/rcm";
          }}
        />
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// RouteErrorFallback — for use as react-router errorElement
// ---------------------------------------------------------------------------

export function RouteErrorFallback() {
  const error = useRouteError();
  const parsed = parseError(error);

  // Log for dev visibility regardless of mode
  console.error("[RouteError]", error);

  return (
    <ErrorDisplay
      message={parsed.message}
      stack={parsed.stack}
      status={parsed.status}
      onRetry={() => window.location.reload()}
      onGoBack={() => window.history.back()}
      onGoHome={() => (window.location.href = "/rcm")}
    />
  );
}
